import { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useFetchIndexFundPriceHistory } from '@/hooks/securities/indexfunds/useIndexFundPriceHistory.ts';

const TIME_RANGES: TimeRange[] = ['1W', '1M', '1Y', 'ALL'];

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatPrice(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const { date, price } = payload[0].payload;
  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3">
      <p className="text-xs text-muted-foreground mb-1">
        {new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>
      <p className="text-sm font-semibold">{formatPrice(price)}</p>
    </div>
  );
}

interface IndexFundNAVTrendChartProps {
  fundSymbol: string;
  period?: TimeRange;
}

export function IndexFundNAVTrendChart({ fundSymbol, period: initialPeriod = '1M' }: IndexFundNAVTrendChartProps) {
  const [period, setPeriod] = useState<TimeRange>(initialPeriod);

  const { data: priceHistory = [], isLoading } = useFetchIndexFundPriceHistory(fundSymbol, period);

  const chartData = useMemo(
    () =>
      [...priceHistory]
        .sort((a, b) => new Date(a.fetched_at).getTime() - new Date(b.fetched_at).getTime())
        .map((r) => ({ date: r.fetched_at, price: r.price })),
    [priceHistory]
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base font-semibold">
          {fundSymbol} NAV History
        </CardTitle>
        <div className="flex gap-1" role="group" aria-label="Time range filter">
          {TIME_RANGES.map((range) => (
            <Button
              key={range}
              variant={period === range ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPeriod(range)}
              aria-pressed={period === range}
            >
              {range}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[280px] flex items-end justify-between gap-2 px-4" aria-busy="true">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="w-full bg-muted animate-pulse rounded-t"
                style={{ height: `${30 + (i % 4) * 15}%` }}
              />
            ))}
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[280px] flex items-center justify-center" data-testid="empty-state">
            <p className="text-muted-foreground text-sm">
              No price history data available for the selected period.
            </p>
          </div>
        ) : (
          <div
            role="img"
            aria-label={`${fundSymbol} NAV trend chart for ${period} period`}
          >
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" tickFormatter={formatDate} className="text-xs" />
                <YAxis tickFormatter={formatPrice} className="text-xs" domain={['auto', 'auto']} />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
